import MultipleAutocomplete from 'components/MultipleAutocomplete';
import { CapitalActionEntryType } from 'further-types/lp/capital-action';
import { CurrencyCode } from 'further-types/lp/vehicle';
import FormField from 'further-ui/components/FormField';
import { IncomeAndExpensesLabels } from 'further-ui/labels';
import { numberToCurrencyString } from 'further-ui/utils';
import api from 'lib/trpcClient';
import { sumBy } from 'lodash';
import { Controller, useFormContext } from 'react-hook-form';
import { CapitalActionFormValues } from '../../hooks/useCapitalActionForm';

type Props = {
  index: number;
  vehicleId: string;
  entryType: CapitalActionEntryType;
  baseCurrency: CurrencyCode;
  disabled?: boolean;
};

const ActionSelector: React.FC<Props> = ({
  index,
  vehicleId,
  entryType,
  baseCurrency,
  disabled,
}) => {
  const { control, setValue, getValues } =
    useFormContext<CapitalActionFormValues>();

  const selectedActionIds = getValues('entries').flatMap((entry, entryIndex) =>
    entryIndex === index ? [] : entry.actions.map((action) => action.id),
  );

  const [availableActions] =
    api.capitalAction.listAvailableEntryActions.useSuspenseQuery({
      vehicleId,
      entryType,
    });

  const options = availableActions
    .filter((action) => !selectedActionIds.includes(action.id))
    .map((action) => ({
      label: `${action.counterpartyLabel} - ${numberToCurrencyString(
        action.uncalledAmountLcy,
        {
          currency: baseCurrency,
        },
      )} - ${action.ledgerDate.toLabel()} ${
        action.type
          ? `(${IncomeAndExpensesLabels[action.type] ?? action.type})`
          : ''
      }${
        action.calledAmountLcy || action.isRealisedFXGainOrLoss
          ? ` Additional balance required`
          : ''
      }`,
      value: action.id,
    }));

  const handleActionsChange = (actionIds: Array<string>) => {
    const currentActions = getValues(`entries.${index}.actions`).filter(
      (action) => actionIds.includes(action.id),
    );
    const newActionIds = actionIds.filter(
      (id) => !currentActions.some((action) => action.id === id),
    );

    setValue(`entries.${index}.actions`, [
      ...currentActions,
      ...newActionIds.map((id) => ({ id, capitalBreakdown: [] })),
    ]);

    const uncalledAmountLcy =
      sumBy(
        availableActions.filter((action) => actionIds.includes(action.id)),
        'uncalledAmountLcy',
      ) ?? 0;

    setValue(`entries.${index}.totalAmount`, uncalledAmountLcy);
  };

  const label = entryType.replaceAll('-', ' ');
  return (
    <FormField label={`Select ${label}`} size="md">
      <Controller
        name={`entries.${index}.actions`}
        control={control}
        render={({ fieldState: { error }, field: { value } }) => (
          <MultipleAutocomplete
            placeholder={`Search for uncalled ${label}`}
            fullWidth
            options={options}
            value={value.map((action) => action.id)}
            onChange={handleActionsChange}
            error={error?.message}
            disabled={disabled}
          />
        )}
      />
    </FormField>
  );
};

export default ActionSelector;
